import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
} from "@mui/material";
import "./task-table.scss";
import CustomTooltip from "../../../../../../../common/components/custom-tooltip";

interface Column<T> {
  label: string;
  render: (row: T) => React.ReactNode;
  key: string;
  onCellClick?: (row: T) => void; // 👈 NEW
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowActions?: (row: T) => React.ReactNode;
  noDataMessage?: string;
  onRowClick?: (row: T) => void;
  fieldName?: string;
}

function DataTable<T extends { id: number }>({
  columns,
  data,
  rowActions,
  noDataMessage,
  onRowClick,
  fieldName,
}: DataTableProps<T>) {
  return (
    <TableContainer
      className={`campaign-modal__table-containers ${
        rowActions ? "has-actions" : ""
      }`}
    >
      <Table
        className={`campaigns-table ${!data.length ? "no-table-border" : ""}`}
      >
        <TableHead>
          <TableRow>
            {rowActions && <TableCell />} {/* Action column header */}
            {columns.map((col) => (
              <TableCell key={col.key}>{col.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length ? (
            data.map((row) => (
              <TableRow
                key={row.id}
                onClick={() => onRowClick?.(row)}
                className={onRowClick ? "task-table-clickable-row" : ""}
              >
                {rowActions && <TableCell>{rowActions(row)}</TableCell>}
                {columns.map((col) => {
                  const isCellClickable = !!col.onCellClick;
                  const cellValue = col.render(row);
                  const isText =
                    typeof cellValue === "string" ||
                    typeof cellValue === "number";

                  return (
                    <TableCell
                      key={col.key}
                      onClick={
                        isCellClickable
                          ? (e: React.MouseEvent<HTMLTableCellElement>) => {
                              e.stopPropagation(); // prevent triggering row onClick
                              col.onCellClick?.(row);
                            }
                          : undefined
                      }
                      className={isCellClickable ? "clickable-cell" : ""}
                      style={{
                        cursor: isCellClickable ? "pointer" : "default",
                      }}
                    >
                      {isText ? (
                        <CustomTooltip
                          title={String(cellValue ?? "")}
                          arrow
                          placement="top-start"
                        >
                          <span>{cellValue}</span>
                        </CustomTooltip>
                      ) : (
                        (cellValue ?? "-")
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length + (rowActions ? 1 : 0)}
                align="center"
              >
                {noDataMessage || "No data available."}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default DataTable;
